// Shared types + constants for the Global Search redesign. Mirrors the server's response shape
// 1:1 (server/src/routes/search.ts) -- keep both in sync when either changes.

export interface SearchPreviewField {
  label: string;
  value: string;
  mono?: boolean;
}

export interface SearchResultData {
  type: string;
  id: number;
  title: string;
  subtitle: string;
  rightLabel: string | null;
  statusLabel: string | null;
  statusTone: 'ok' | 'warn' | 'neutral';
  thumbnailUrl: string | null;
  previewImageUrls?: string[];
  route: string;
  previewFields: SearchPreviewField[];
  // Catalog-only passthrough so the "Add to order" preview action can fetch the real, full
  // InventoryItem (GET /inventory/items/:id) before opening the existing AddToOrderModal.
  _sourcingStatus?: string;
}

export interface SearchResponseData {
  results: SearchResultData[];
  counts: Record<string, number>;
  total: number;
}

// The design's six first-class types get a fixed semantic color; everything else (keys, tasks,
// schedule, extinguishers, panels, breakers -- every type the OLD search already supported) gets
// one shared neutral color and its own lucide icon instead of a photo.
export const TYPE_LABELS: Record<string, string> = {
  catalogue: 'Catalog',
  supply: 'Supplies',
  equipment: 'Equipment',
  vendor: 'Vendors',
  order: 'Orders',
  work_order: 'Work orders',
  task: 'Tasks',
  key: 'Keys',
  schedule: 'Calendar',
  extinguisher: 'Extinguishers',
  breaker_panel: 'Panels',
  breaker: 'Breakers',
};

export const TYPE_COLORS: Record<string, string> = {
  catalogue: '#60A5FA',
  supply: '#34D399',
  equipment: '#FB923C',
  vendor: '#A78BFA',
  order: '#FBBF24',
  work_order: '#F472B6',
};
export const NEUTRAL_TYPE_COLOR = '#94A3B8';
export function typeColor(type: string): string {
  return TYPE_COLORS[type] ?? NEUTRAL_TYPE_COLOR;
}

// Preferred chip/section display order -- the design's six first, then the legacy types kept
// for coverage. Any type the server returns that isn't listed here still renders (falls through
// to the end, sorted by first appearance) -- this is a display preference, not a filter.
export const TYPE_ORDER = [
  'catalogue', 'supply', 'equipment', 'vendor', 'order', 'work_order',
  'task', 'key', 'schedule', 'extinguisher', 'breaker_panel', 'breaker',
];

export function sortTypes(types: string[]): string[] {
  return [...types].sort((a, b) => {
    const ia = TYPE_ORDER.indexOf(a); const ib = TYPE_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return 0;
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
}

// ─── Recent searches / recently viewed (localStorage, best-effort) ────────────────────────────
const RECENT_SEARCHES_KEY = 'pq_recent_searches';
const RECENTLY_VIEWED_KEY = 'pq_recently_viewed';

export function getRecentSearches(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

export function pushRecentSearch(q: string) {
  try {
    const trimmed = q.trim();
    if (!trimmed) return;
    const cur = getRecentSearches().filter((s) => s.toLowerCase() !== trimmed.toLowerCase());
    cur.unshift(trimmed);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(cur.slice(0, 6)));
  } catch { /* localStorage unavailable -- not critical, just skip persisting */ }
}

export interface RecentlyViewedEntry {
  type: string; id: number; title: string; thumbnailUrl: string | null; route: string;
}

export function getRecentlyViewed(): RecentlyViewedEntry[] {
  try {
    const raw = localStorage.getItem(RECENTLY_VIEWED_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

export function pushRecentlyViewed(r: SearchResultData) {
  try {
    const cur = getRecentlyViewed().filter((e) => !(e.type === r.type && e.id === r.id));
    cur.unshift({ type: r.type, id: r.id, title: r.title, thumbnailUrl: r.thumbnailUrl, route: r.route });
    localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(cur.slice(0, 5)));
  } catch { /* ignore */ }
}

// ─── Query highlighting ─────────────────────────────────────────────────────────
// Splits `text` into plain/matched segments for every query token (case-insensitive), so the
// caller can render matched segments in a <mark>. Returns an array of
// { text, matched } segments in original order -- never reorders or drops characters.
export interface HighlightSegment { text: string; matched: boolean }

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function highlightSegments(text: string, tokens: string[]): HighlightSegment[] {
  const cleanTokens = tokens.map((t) => t.trim()).filter(Boolean);
  if (!text || cleanTokens.length === 0) return [{ text, matched: false }];
  const pattern = new RegExp(`(${cleanTokens.map(escapeRegExp).join('|')})`, 'gi');
  const parts = text.split(pattern);
  return parts.filter((p) => p !== '').map((p) => ({
    text: p,
    matched: cleanTokens.some((t) => p.toLowerCase() === t.toLowerCase()),
  }));
}
